// hooks/auth/useForgotPassword.ts
import { useState, useEffect, useCallback } from 'react';

// Import Orval-generated API clients
import { 
    useInitiatePasswordReset, 
    useVerifyOtp, 
    useResetPassword1,
    useResendOtp
} from '@/shared/api/client/forgot-password';
import {ForgotPasswordRequestDTO, ResetPasswordDTO} from "@/shared/api/model";

export const useForgotPassword = () => {
    const [isOtpRequired, setIsOtpRequired] = useState(true);
    const [maskedPhoneNumber, setMaskedPhoneNumber] = useState('');
    const [resetToken, setResetToken] = useState('');
    const [canResendAfter, setCanResendAfter] = useState<Date | null>(null);
    const [countdown, setCountdown] = useState(0);
    const [canResend, setCanResend] = useState(false);

    // Countdown timer effect
    useEffect(() => {
        let interval: NodeJS.Timeout;
        
        if (canResendAfter) {
            interval = setInterval(() => {
                const now = new Date().getTime();
                const resendTime = new Date(canResendAfter).getTime();
                const timeLeft = Math.max(0, Math.ceil((resendTime - now) / 1000));
                
                console.log("Timer tick - Time left:", timeLeft);
                setCountdown(timeLeft);
                setCanResend(timeLeft === 0);
                
                if (timeLeft === 0) {
                    clearInterval(interval);
                    console.log("Timer finished, clearing interval");
                }
            }, 1000);
        } else {
            // If no canResendAfter, allow immediate resend
            setCanResend(true);
            setCountdown(0);
        }
        
        return () => {
            if (interval) {
                console.log("Cleaning up timer interval");
                clearInterval(interval);
            }
        };
    }, [canResendAfter]);

    const updateResendState = useCallback((canResendAfterTime: string | Date | null) => {
        console.log("updateResendState called with:", canResendAfterTime);
        if (canResendAfterTime) {
            const resendTime = new Date(canResendAfterTime);
            const now = new Date();
            const timeLeft = Math.max(0, Math.ceil((resendTime.getTime() - now.getTime()) / 1000));
            
            console.log("Resend time:", resendTime, "Now:", now, "Time left:", timeLeft);
            
            // If timeLeft is 0 or very small, set a minimum countdown of 20 seconds
            const finalTimeLeft = timeLeft <= 5 ? 20 : timeLeft;
            const finalResendTime = timeLeft <= 5 ? new Date(now.getTime() + 20000) : resendTime;
            
            console.log("Final time left:", finalTimeLeft, "Final resend time:", finalResendTime);
            
            setCanResendAfter(finalResendTime);
            setCountdown(finalTimeLeft);
            setCanResend(finalTimeLeft === 0);
        } else {
            console.log("No canResendAfter time provided, setting 20 second default countdown");
            // If no canResendAfter provided, set a default 20-second countdown
            const defaultResendTime = new Date(new Date().getTime() + 20000);
            setCanResendAfter(defaultResendTime);
            setCountdown(20);
            setCanResend(false);
        }
    }, []);

    const initiatePasswordResetMutation = useInitiatePasswordReset({
        mutation: {
            onSuccess: (response) => {
                const data = response;
                console.log("initiate password reset", data);
                setIsOtpRequired(data?.otpRequired || false);
                setMaskedPhoneNumber(data?.maskedPhoneNumber || '');
                setResetToken(data?.resetToken || '');
                // @ts-ignore
                updateResendState(data?.canResendAfter);
            }
        }
    });

    const verifyOtpMutation = useVerifyOtp();
    const resetPasswordMutation = useResetPassword1();
    
    const resendOtpMutation = useResendOtp({
        mutation: {
            onSuccess: (response) => {
                const data = response;
                console.log("resend otp",data);
                setMaskedPhoneNumber(data?.maskedPhoneNumber || '');
                // @ts-ignore
                updateResendState(data?.canResendAfter);
            }
        }
    });

    return {
        initiatePasswordReset: (request: ForgotPasswordRequestDTO) =>
            initiatePasswordResetMutation.mutateAsync({ data: request }),
            
        verifyOtp: ({ resetToken, otp }: { resetToken: string; otp: string }) => 
            verifyOtpMutation.mutateAsync({ 
                params: { resetToken, otp } 
            }).then(response => response),
            
        resetPassword: (resetPasswordDTO: ResetPasswordDTO) => 
            resetPasswordMutation.mutateAsync({ data: resetPasswordDTO })
                .then(response => response),
                
        resendOtp: () => {
            if (!resetToken) {
                throw new Error('No reset token available');
            }
            return resendOtpMutation.mutateAsync({ 
                params: { resetToken } 
            });
        },
                
        isOtpRequired,
        maskedPhoneNumber,
        resetToken,
        canResend,
        countdown,
        isResending: resendOtpMutation.isPending,
    };
};